import LinkArrow from "@husky-hrc/Components/Icons/LinkArrow";
import { ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import { resolveColor, safeColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

type ButtonShapes = "slanted" | "regular";
interface SlantedButtonIconProps {
    src: React.ComponentType | string;
    position: "right" | "left";
}

export const slantedButtonIconProps: SlantedButtonIconProps = {
    src: LinkArrow,
    position: "right",
};

export const themeButtonBaseProps: ButtonPresentationProps = {
    size: 46,
    buttonType: "solid",
    shape: "rectangle",
    mergeCss: true,
    typographyProps: {
        color: "text.main",
        size: 16,
        weight: 700,
    },
};
const buttonColorStyles = (buttonVariant: ButtonVariants) => {
    switch (buttonVariant) {
        case "primary":
            return css`
                background-color: ${getColor("primary.main")};

                span,
                svg,
                & {
                    color: ${getColor("text.main")};
                }
            `;
        case "secondary":
            return css`
                background-color: ${getColor("text.main")};

                span,
                svg,
                & {
                    color: white;
                }

                &:hover {
                    background-color: ${props => safeColor(getColor("text.main"))["lightness"](34).string()};
                }

                &:active {
                    background-color: ${props => safeColor(getColor("text.main"))["lightness"](62).string()};

                    span,
                    svg,
                    & {
                        color: ${getColor("text.main")};
                    }
                }
            `;
        case "tertiary":
            return css`
                background-color: ${getColor("text.disabled")};

                span,
                svg,
                & {
                    color: white;
                }

                &:hover {
                    background-color: ${props => safeColor(getColor("text.disabled"))["lightness"](34).string()};
                }

                &:active {
                    background-color: ${props => safeColor(getColor("text.disabled"))["lightness"](62).string()};

                    span,
                    svg,
                    & {
                        color: ${getColor("text.disabled")};
                    }
                }
            `;
        default:
            break;
    }
};

const buttonSlantColorStyles = (buttonVariant: ButtonVariants) => {
    switch (buttonVariant) {
        case "primary":
            return css`
                &:after {
                    background-color: ${getColor("primary.main")};
                }

                &:hover:after {
                    background-color: ${props =>
                        safeColor(resolveColor("primary.main", props.theme))["darken"](0.3).string()};
                }

                &:active:after {
                    background-color: ${props =>
                        safeColor(resolveColor("primary.main", props.theme))["darken"](0.6).string()};
                }
            `;
        case "secondary":
            return css`
                &:after {
                    background-color: ${getColor("text.main")};
                }

                &:hover:after {
                    background-color: ${props => safeColor(getColor("text.main"))["lightness"](34).string()};
                }

                &:active:after {
                    background-color: ${props => safeColor(getColor("text.main"))["lightness"](62).string()};
                }
            `;
        case "tertiary":
            return css`
                &:after {
                    background-color: ${getColor("text.disabled")};
                }

                &:hover:after {
                    background-color: ${props => safeColor(getColor("text.disabled"))["lightness"](34).string()};
                }

                &:active:after {
                    background-color: ${props => safeColor(getColor("text.disabled"))["lightness"](62).string()};
                }
            `;
        default:
            break;
    }
};

const buttonBaseCssStyles = (buttonVariant: ButtonVariants) => {
    return css`
        position: relative;
        border: none;
        border-radius: 0;
        transition: transform 0.25s ease-in-out, background 0.25s ease-in-out;
        padding: 15px;
        height: auto;
        min-height: auto;
        line-height: 1.2;

        &:disabled {
            background-color: ${getColor("common.disabled")} !important;

            &:after {
                display: none;
            }
        }
    `;
};

export const buttonBaseStyles = (buttonShape?: ButtonShapes) => {
    switch (buttonShape) {
        case "slanted":
            return css`
                ${(props: any) => {
                    const variant = props.variant ? props.variant : "primary";
                    return css`
                        ${buttonBaseCssStyles(variant)}
                        ${buttonColorStyles(variant)}
                    ${buttonSlantColorStyles(variant)}
                    `;
                }}
                padding-right: 50px;
                text-align: left;

                ${IconWrapper} {
                    position: absolute;
                    top: 50%;
                    right: 5px;
                    transform: translate(0px, -50%);
                    z-index: 2;
                    transition: all 0.25s ease-in-out;

                    &,
                    > svg {
                        width: 35px;
                        height: 35px;
                    }
                }

                &:after {
                    content: "";
                    display: block;
                    position: absolute;
                    right: -13px;
                    width: 25px;
                    height: auto;
                    transform: skew(-10deg);
                    top: 0;
                    bottom: 0;
                    transition: transform 0.25s ease-in-out, background 0.25s ease-in-out;
                }

                &:not(:disabled):hover {
                    ${IconWrapper} {
                        transform: translate(16px, -50%);
                    }
                }

                &:focus {
                    outline-offset: 0;

                    &:after {
                        top: -2px;
                        bottom: -2px;
                        border: 2px solid ${props => props.theme.focus.color};
                        border-left: 0;
                    }
                }
            `;
        case "regular":
            return css`
                ${(props: any) => {
                    const variant = props.variant ? props.variant : "primary";
                    return css`
                        ${buttonBaseCssStyles(variant)}
                        ${buttonColorStyles(variant)}
                    `;
                }}
            `;
        default:
            return css`
                ${(props: any) => {
                    const variant = props.variant ? props.variant : "primary";
                    return css`
                        ${buttonBaseCssStyles(variant)}
                        ${buttonColorStyles(variant)}
                    `;
                }}
            `;
    }
};

export const huskyThemedButtonProps = (
    btnVariant?: ButtonVariants,
    btnShape?: ButtonShapes,
    extraCss?: StyledProp,
    overrideProps?: ButtonPresentationProps,
) => {
    const shape = btnShape || "regular";
    const basicCss = css`
        ${buttonBaseStyles(shape)}
        ${extraCss}
    `;
    const buttonProps: ButtonPresentationProps = {
        ...themeButtonBaseProps,
        variant: btnVariant || "primary",
        css: basicCss,
        ...overrideProps,
    };
    if (shape === "slanted") {
        buttonProps.icon = slantedButtonIconProps;
    }
    return buttonProps;
};
